import React, { useId } from 'react'
import classNames from 'classnames'
import { Icon } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import { DateTime } from 'luxon'

import { BulletinListResponse } from '../../../common/generated/residentApiSchemas'
import { BulletinBoardLocationState } from './bulletinBoardLocationState'

import { BulletinDetails } from 'components/Bulletin/components/BulletinDetails/BulletinDetails'
import { StyledLink } from 'components/StyledLink/StyledLink'
import { Error } from 'components/Error'
import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'

const YEAR_SEARCH_PARAM = 'year'
const ALL = 'all'

type BulletinListItem = BulletinListResponse['bulletins'][number]

// Luxon's default zone is Europe/Helsinki, so the year is in Finnish time
const publishedYear = (bulletin: BulletinListItem) =>
  DateTime.fromISO(bulletin.publishedAt).year.toString()

interface FilterPillsProps {
  label: string
  options: { id: string; label: string }[]
  selectedId: string
  onChange: (id: string) => void
}

const FilterPills: React.FC<FilterPillsProps> = ({
  label,
  options,
  selectedId,
  onChange,
}) => {
  const labelId = useId()

  return (
    <div className="flex flex-col gap-2">
      <span id={labelId} className="text-sm text-common-gray-7">
        {label}
      </span>
      <div
        role="group"
        aria-labelledby={labelId}
        className="flex gap-2 overflow-x-auto"
      >
        {options.map(({ id, label: optionLabel }) => {
          const isSelected = id === selectedId

          return (
            <button
              key={id}
              type="button"
              aria-pressed={isSelected}
              onClick={() => onChange(id)}
              className={classNames(
                'h-9 px-4 shrink-0 whitespace-nowrap rounded-full border',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                {
                  'bg-brand-6 border-brand-6 text-white font-semibold':
                    isSelected,
                  'bg-white border-common-gray-4 text-common-gray-7':
                    !isSelected,
                }
              )}
            >
              {optionLabel}
            </button>
          )
        })}
      </div>
    </div>
  )
}

interface BulletinBoardListProps {
  bulletins: BulletinListItem[]
}

export const BulletinBoardList: React.FC<BulletinBoardListProps> = ({
  bulletins,
}) => {
  const { t } = useTranslation(['bulletins'])
  const { bulletinBoardBulletinPath } = useContractPaths()
  const [searchParams, setSearchParams] = useSearchParams()

  if (!bulletins.length) {
    return (
      <Error
        illustration="empty-letter"
        message={t('bulletins:errors.noBulletins')}
      />
    )
  }

  // Bulletins come sorted newest first, so the years are too
  const years = [...new Set(bulletins.map(publishedYear))]
  const yearParam = searchParams.get(YEAR_SEARCH_PARAM)
  const selectedYear = yearParam && years.includes(yearParam) ? yearParam : ALL

  const visibleBulletins =
    selectedYear === ALL
      ? bulletins
      : bulletins.filter((bulletin) => publishedYear(bulletin) === selectedYear)

  const handleFilterChange = (searchParam: string, value: string) => {
    setSearchParams(
      (params) => {
        if (value === ALL) {
          params.delete(searchParam)
        } else {
          params.set(searchParam, value)
        }
        return params
      },
      { replace: true }
    )
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 pb-6 border-b border-common-gray-4">
        <FilterPills
          label={t('bulletins:year')}
          options={[
            { id: ALL, label: t('bulletins:allYears') },
            ...years.map((year) => ({ id: year, label: year })),
          ]}
          selectedId={selectedYear}
          onChange={(year) => handleFilterChange(YEAR_SEARCH_PARAM, year)}
        />
      </div>
      <div className="flex flex-col gap-6">
        {visibleBulletins.map((bulletin) => (
          <div key={bulletin.uuid} className="flex flex-col gap-1">
            <div className="flex items-center group">
              <StyledLink
                to={bulletinBoardBulletinPath.replace(
                  ':bulletinUuid',
                  bulletin.uuid
                )}
                state={
                  {
                    bulletinBoardSearch: searchParams.toString(),
                  } satisfies BulletinBoardLocationState
                }
              >
                {bulletin.title}
              </StyledLink>
              <Icon
                icon="angle-right"
                size="sm"
                className="ml-2 text-common-gray-7"
              />
            </div>
            <BulletinDetails
              publishedAt={bulletin.publishedAt}
              updatedAt={bulletin.updatedAt}
              author={bulletin.author}
            />
          </div>
        ))}
      </div>
    </div>
  )
}
